import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { useEventListener } from 'ahooks';
import { Icon } from '@shakkerai/components';
import ImgTitleBg from '../../assets/title_bg.png';
import { communityUrl } from '../../constants';
import { translate } from '@shakkerai/util-i18n';
import AniInput from './AniInput';
import { setSharedData, setTempState } from '@starflow/app/store';
import { getTempState } from '@shakkerai/pro-store';

export const Tab = React.memo(({ options, value, onChange }) => {
  return (
    <div className="flex items-center space-x-8">
      {options.map((option) => {
        return (
          <button
            key={option.value}
            disabled={value === option.value}
            onClick={() => onChange(option.value)}
            className={`flex items-center h-[52px] pt-1 border-b-[3px] text-sm font-semibold text-[#adadad] border-transparent disabled:border-primary disabled:text-[#2d2d2d] cursor-pointer`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
});

export const ModelWindowWrapper = React.memo((props) => {
  const { label, type, currentTab } = props;
  const containerRef = useRef(null);
  const [position, setPosition] = useState(null);

  const handleViewModels = useCallback(() => {
    window.open(communityUrl, '_blank');
  }, []);

  const handleCloseWindow = useCallback(() => {
    setTempState({ exclusiveWindow: null });
  }, []);

  const handleGlobalClick = useCallback((event) => {
    if (containerRef.current?.contains(event.target)) {
      return;
    }

    if (event.target?.closest(`.images-window`)) {
      return;
    }

    if (event.target?.closest(`.imginfo-window`)) {
      return;
    }

    if (event.target?.closest(`[data-trigger-window="model-${type}"]`)) {
      return;
    }

    if (event.target?.closest('.mantine-Popover-dropdown')) {
      return;
    }

    setTempState({ exclusiveWindow: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener('click', handleGlobalClick);

  useEffect(() => {
    const entryButtonId = getTempState('tempPickingButtonId') || `model-${type}-entry`;
    const entryButton = document.getElementById(entryButtonId);

    const { x } = entryButton ? entryButton.getBoundingClientRect() : { x: 1052 };

    setPosition({ x: x - 1002, y: 55 });
  }, [type]);

  useEffect(() => {
    return () => {
      setTempState({
        tempPickingModel: false,
        tempCheckpoint: null,
        tempCheckpointPickCallback: null,
        tempLoras: null,
        tempLoraPickCallback: null,
      });
    };
  }, []);

  if (!position) {
    return null;
  }

  return createPortal(
    <Rnd default={position} scale={1} className="z-[100]" dragHandleClassName="sub-drag-handle">
      <div
        ref={containerRef}
        data-role="models-window"
        className="flex flex-col items-stretch w-[980px] h-[650px] max-h-[80vh] bg-white shadow-light rounded-[2px]"
      >
        <header className="relative sub-drag-handle flex flex-shrink-0 items-center justify-end h-[52px] px-4 cursor-move border-b border-[#efefef]">
          <div className="absolute top-0 left-0 w-40 h-8 flex items-center">
            <span className="relative z-[2] ml-6 text-mini font-semibold text-white">{label}</span>
            <img src={ImgTitleBg} className="absolute z-[1] top-0 left-0 w-40 h-8" draggable={false} />
          </div>
          <div className="ml-[171px]">{props.tabs}</div>
          <div className="flex-1" />
          {type === 'loras' && currentTab === 'all' ? <AniInput /> : null}
          <button onClick={handleViewModels} className="flex items-center mr-5">
            <Icon className="shk-danchuang-moxingguangchang text-primary" fontSize={14} />
            <span className="ml-1 translate-y-px text-sm text-primary">{translate('basemodel_toshakker3')}</span>
          </button>
          <button onClick={handleCloseWindow}>
            <Icon fontSize={16} className="icon-border_close-1 text-[#222]" />
          </button>
        </header>
        {props.children}
      </div>
    </Rnd>,
    document.body,
  );
});
